/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  ASSISTANT_DESCRIPTION_MAX_LENGTH,
  ASSISTANT_DESCRIPTION_MIN_LENGTH,
  ASSISTANT_DESCRIPTION_UPDATE_MAX_LENGTH,
  ASSISTANT_INTRO_MESSAGE_MAX_LENGTH,
  ASSISTANT_INTRO_MESSAGE_MIN_LENGTH,
  ASSISTANT_NAME_MAX_LENGTH,
  ASSISTANT_NAME_MIN_LENGTH,
  GENERIC_ASSISTANT_CONFIGURATION_FILENAME,
} from '@shared';
import { environment } from '../../../../environments/environment';

// Re-export shared constants with shorter names for form usage
export const NAME_MIN_LENGTH = ASSISTANT_NAME_MIN_LENGTH;
export const NAME_MAX_LENGTH = ASSISTANT_NAME_MAX_LENGTH;
export const DESCRIPTION_MIN_LENGTH = ASSISTANT_DESCRIPTION_MIN_LENGTH;
/** Create-strict description cap (KD-7). */
export const DESCRIPTION_MAX_LENGTH = ASSISTANT_DESCRIPTION_MAX_LENGTH;
/** Update hard-cap: grandfathered descriptions stay editable up to this (KD-7). */
export const DESCRIPTION_UPDATE_MAX_LENGTH =
  ASSISTANT_DESCRIPTION_UPDATE_MAX_LENGTH;
export const INTRO_MESSAGE_MIN_LENGTH = ASSISTANT_INTRO_MESSAGE_MIN_LENGTH;
export const INTRO_MESSAGE_MAX_LENGTH = ASSISTANT_INTRO_MESSAGE_MAX_LENGTH;
export const ASSISTANT_PROMPT_MAX_LENGTH = environment.assistantPromptMaxLength;

/**
 * Validates minLength only when the control has a non-empty value.
 * Useful for optional fields like introMessage: allow empty, but if provided must be >= minLength.
 */
export function minLengthWhenNotEmpty(
  minLength: number,
): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || control.value.length === 0) {
      return null;
    }
    if (control.value.length < minLength) {
      return {
        minlength: {
          requiredLength: minLength,
          actualLength: control.value.length,
        },
      };
    }
    return null;
  };
}

/**
 * Description max-length validator that mirrors the server's grandfather-on-edit
 * rule (KD-7 / FM-3, `assistants.controller.ts`): the create-strict cap
 * (`ASSISTANT_DESCRIPTION_MAX_LENGTH`, 140) is enforced only on a description
 * that actually CHANGED from its stored per-locale baseline. An unchanged
 * (pristine) legacy value equal to `storedValue` stays valid regardless of
 * length — so an untouched over-cap description neither errors nor blocks
 * saving other fields. In create mode there is no baseline (`storedValue` is
 * `null`), so every value is held to the strict cap.
 *
 * Returns the standard `{ maxlength: { requiredLength, actualLength } }` shape
 * so the existing field-error template renders the shared MAX_LENGTH message.
 * The absolute update hard-ceiling (`ASSISTANT_DESCRIPTION_UPDATE_MAX_LENGTH`,
 * 2000) is enforced separately by the field's display limit.
 */
export function grandfatheredDescriptionMaxLength(
  storedValue: string | null | undefined,
): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = (control.value ?? '') as string;
    // Pristine legacy value (edit): grandfathered — exactly as the server
    // passes an unchanged description through untouched.
    if (storedValue != null && value === storedValue) {
      return null;
    }
    // Edited value, or create (no baseline): create-strict cap.
    if (value.length > DESCRIPTION_MAX_LENGTH) {
      return {
        maxlength: {
          requiredLength: DESCRIPTION_MAX_LENGTH,
          actualLength: value.length,
        },
      };
    }
    return null;
  };
}

/**
 * Checks whether a translation entry has enough data to be considered valid
 * (i.e. it has a name and description at minimum).
 */
export function isValidTranslation(translation: {
  name?: string;
  description?: string;
}): boolean {
  return !!translation.name && !!translation.description;
}

/**
 * Maps a form control's validation errors to a translated error message string.
 * Resolves the first error found (priority: required > minlength > maxlength > custom).
 */
export function getFormErrorMessage(
  control: AbstractControl | null,
  translate: TranslateService,
): string {
  if (!control?.errors) {
    return '';
  }

  const errors = control.errors;
  const prefix = 'ASSISTANTS.SAVE_MODAL.FORM.ERRORS';

  if (errors['required']) {
    return translate.instant(`${prefix}.REQUIRED`) as string;
  }
  if (errors['minlength']) {
    return translate.instant(`${prefix}.MIN_LENGTH`, {
      min: errors['minlength'].requiredLength,
    }) as string;
  }
  if (errors['maxlength']) {
    return translate.instant(`${prefix}.MAX_LENGTH`, {
      max: errors['maxlength'].requiredLength,
    }) as string;
  }
  if (errors['filesRequired']) {
    return translate.instant(`${prefix}.FILES_REQUIRED`) as string;
  }
  if (errors['skillMdConfigurationFile']) {
    return translate.instant(`${prefix}.SKILL_MD_CONFIGURATION_FILE`, {
      configFile: GENERIC_ASSISTANT_CONFIGURATION_FILENAME,
    }) as string;
  }
  if (errors['skillMdFilenameNearMiss']) {
    return translate.instant(`${prefix}.SKILL_MD_FILENAME_NEAR_MISS`, {
      configFile: GENERIC_ASSISTANT_CONFIGURATION_FILENAME,
      actual: errors['skillMdFilenameNearMiss'].actual,
    }) as string;
  }

  return translate.instant(`${prefix}.INVALID`) as string;
}
